<script>
import parseDate from 'date-fns/parse'
import lazyLoadComponent from '@router/lazyload-component'

import Layout from '@layouts/main'
import BaseButtonDialog from '@components/BaseButtonDialog'

import pageMixin from '@src/mixins/pagination'

import {
  productTypeList,
  productTypeFilter,
  prepaidCapacityFilter,
  defaultStatusFilter,
} from '@utils/filter-value-to-text'
import { formatDateRange } from '@utils/format-date'

export default {
  page: {
    title: '預付方案管理',
    meta: [{ name: 'description', content: 'Prepaid List' }],
  },
  components: {
    Layout,
    BaseButtonDialog,
    BaseStateLabel: () =>
      lazyLoadComponent(import('@components/BaseStateLabel')),
  },
  filters: {
    productTypeFilter,
    defaultStatusFilter,
    prepaidCapacityFilter,
    formatDateRange,
  },
  mixins: [pageMixin],
  data() {
    return {
      tableData: [],
      selectedCoupons: [],
      filterData: {
        state: '',
        productType: '',
      },
      productTypeList: Object.freeze(productTypeList),
    }
  },
  computed: {
    enabledBulkButton() {
      return this.selectedCoupons.length === 0
    },
    enabledCopyButton() {
      return this.selectedCoupons.length === 1
    },
  },
  watch: {
    currentPage: {
      handler: 'tryToFetchList',
      immediate: true,
    },
  },
  methods: {
    async tryToFetchList() {
      const { items, total } = await this.$store.dispatch(
        'prepaid/fetchList',
        Object.assign(this.filterData, this.pageQuery)
      )
      this.tableData = items
      this.$nextTick(() => {
        this.$refs.pagination.updateTotal(total)
      })
    },
    checkAvailability(row) {
      const now = new Date()
      const { state, startDate, endDate } = row
      if (state === 0) return false

      if (startDate && endDate)
        return now >= parseDate(startDate) && now <= parseDate(endDate)
      if (!startDate) return now <= parseDate(endDate)
      if (!endDate) return now >= parseDate(startDate)
      return state
    },
    goToViewPage(row) {
      const prepaidId = row.id
      this.$router.push({ name: 'view-prepaid', params: { prepaidId } })
    },
    goToAddPrepaid() {
      this.$router.push({ name: 'add-prepaid' })
    },
    async handleBulkUpdateState(state) {
      const prepaidIds = this.selectedCoupons.map(({ id }) => id)
      await this.$store.dispatch('prepaid/updateState', {
        ids: prepaidIds,
        state,
      })
      this.tryToFetchList()
    },
    async handleDeleteCoupon() {
      const prepaidIds = this.selectedCoupons.map(({ id }) => id)
      await this.$store.dispatch('prepaid/remove', { ids: prepaidIds })
      this.tryToFetchList()
    },
    async handleDuplicateCoupon() {
      const [prepaidId] = this.selectedCoupons.map(({ id }) => id)

      const { id, title, alias, ...prepaidData } = await this.$store.dispatch(
        'prepaid/fetchSingle',
        prepaidId
      )

      const duplicateData = {
        title: `${title}（複製）`,
        ...prepaidData,
      }

      await this.$store.dispatch('prepaid/store', duplicateData)
      this.tryToFetchList()
    },
    handleSelectionChange(selected) {
      this.selectedCoupons = selected
    },
  },
}
</script>

<template>
  <Layout>
    <BasePageHeader>
      <template v-slot:title>預付方案管理</template>
      <template v-slot:right>
        <el-button :disabled="enabledBulkButton" @click="handleBulkUpdateState(1)">
          <BaseIcon name="eye" />
          <span>變更為開放</span>
        </el-button>
        <el-button :disabled="enabledBulkButton" @click="handleBulkUpdateState(0)">
          <BaseIcon name="eye-slash" />
          <span>變更為不開放</span>
        </el-button>
        <el-button type="danger" :disabled="enabledBulkButton" @click="handleDeleteCoupon">
          <BaseIcon name="trash-alt" />
          <span>刪除選取</span>
        </el-button>
        <el-button :disabled="!enabledCopyButton" @click="handleDuplicateCoupon">
          <BaseIcon name="copy" />
          <span>複製</span>
        </el-button>
      </template>
    </BasePageHeader>
    <BaseTableSearch v-slot="{ fields }" :default-value="filterData" @reset="tryToFetchList">
      <el-form-item prop="state">
        <el-select v-model="fields.state" placeholder="選擇開放狀態" @change="tryToFetchList">
          <el-option label="開放中" value="1"></el-option>
          <el-option label="未開放" value="0"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item prop="productType">
        <el-select v-model="fields.productType" placeholder="適用項目類型" @change="tryToFetchList">
          <el-option
            v-for="(type, typeValue) in productTypeList"
            :key="typeValue"
            :label="type.name"
            :value="typeValue"
          ></el-option>
        </el-select>
      </el-form-item>
    </BaseTableSearch>
    <el-table
      :data="tableData"
      :class="$style['table']"
      @row-click.once="goToViewPage"
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="selection" width="55"></el-table-column>
      <el-table-column v-slot="{ row }" label="圖片">
        <el-image slot="error" class="image image--square" :src="row.introimage" fit="cover">
          <BaseIcon name="image" class="image--error" />
        </el-image>
      </el-table-column>
      <el-table-column label="標題" prop="title"></el-table-column>
      <el-table-column v-slot="{ row }" label="開放狀態">
        <BaseStateLabel
          :state="row.state | defaultStatusFilter('color') "
          text
        >{{ row.state | defaultStatusFilter('text') }}</BaseStateLabel>
      </el-table-column>
      <el-table-column v-slot="{ row }" label="適用項目類型">{{ row.productType | productTypeFilter}}</el-table-column>
      <el-table-column v-slot="{ row }" label="售價">{{ `$${row.price}`}}</el-table-column>
      <el-table-column label="可使用次數" prop="times"></el-table-column>
      <el-table-column
        v-slot="{ row }"
        label="販售狀態"
      >{{ row.infinity | prepaidCapacityFilter(row.qty, row.inventory)}}</el-table-column>

      <el-table-column width="50">
        <el-tooltip content="查看預付方案詳情" placement="top">
          <el-button type="text">
            <BaseIcon name="ellipsis-v" />
          </el-button>
        </el-tooltip>
      </el-table-column>
    </el-table>
    <BasePagination ref="pagination" :current-page.sync="currentPage" />
    <BaseButtonDialog button-tooltip="新增預付方案" only-button floating @opened="goToAddPrepaid" />
  </Layout>
</template>

<style lang="scss" module>
@import '@design';
.table {
  @include layer-elevation(5);
}
</style>
